// AccessControlContext.tsx

import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  isCountryAuthorized,
  CountryAuthorization,
} from '../../utils/isCountryAuthorized';

// Shape of the context that will be exposed to the rest of the app
interface AccessControlContextType {
  accessGranted: boolean;
  countryIso2: string | null;
  countryName: string | null;
}

// Initial default values used before any async loading
const initialContextValue: AccessControlContextType = {
  accessGranted: true,
  countryIso2: null,
  countryName: null,
};

// Key used to store and retrieve country info in localStorage
const COUNTRY_STORAGE_KEY = 'countryAuthorization';

// Actual React context instance
const AccessControlContext =
  createContext<AccessControlContextType>(initialContextValue);

// Convenience hook to consume the context
export const useAccessControl = () => useContext(AccessControlContext);

// Props for the provider component
interface AccessControlProviderProps {
  children: ReactNode;
}

/**
 * AccessControlProvider
 *
 * - Calls the backend endpoint /localisation/is-authorized via isCountryAuthorized()
 * - Stores the result both in React state and in localStorage
 * - Exposes:
 *    - accessGranted: whether the user is allowed to access the app
 *    - countryIso2: detected ISO2 country code (e.g. "FR", "US")
 *    - countryName: detected country name (e.g. "France", "United States")
 *
 * The data is:
 *   1. Initially read from localStorage (if present)
 *   2. Then refreshed from the API to ensure it is up to date
 */
export const AccessControlProvider: React.FC<AccessControlProviderProps> = ({
  children,
}) => {
  const [accessGranted, setAccessGranted] = useState<boolean>(
    initialContextValue.accessGranted,
  );
  const [countryIso2, setCountryIso2] = useState<string | null>(
    initialContextValue.countryIso2,
  );
  const [countryName, setCountryName] = useState<string | null>(
    initialContextValue.countryName,
  );

  useEffect(() => {
    // 1) Try to hydrate state from localStorage first (fast, no network)
    if (typeof window !== 'undefined') {
      const fromStorage = localStorage.getItem(COUNTRY_STORAGE_KEY);

      if (fromStorage) {
        try {
          const parsed = JSON.parse(fromStorage) as CountryAuthorization;

          setAccessGranted(parsed.authorized);
          setCountryIso2(parsed.countryIso2 ?? null);
          setCountryName(parsed.countryName ?? null);
        } catch (e) {
          console.error(
            'Failed to parse country authorization from localStorage:',
            e,
          );
        }
      }
    }

    // 2) Then refresh from backend to ensure the data is accurate
    const checkAuthorization = async () => {
      try {
        const result: CountryAuthorization = await isCountryAuthorized();

        setAccessGranted(result.authorized);
        setCountryIso2(result.countryIso2 ?? null);
        setCountryName(result.countryName ?? null);

        // Persist the latest state in localStorage for future sessions
        if (typeof window !== 'undefined') {
          localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(result));
        }
      } catch (error) {
        console.error(
          'Error while checking country authorization:',
          error,
        );
        // In case of error, deny access by default (fail closed)
        setAccessGranted(false);
      }
    };

    checkAuthorization();
  }, []);

  return (
    <AccessControlContext.Provider
      value={{ accessGranted, countryIso2, countryName }}
    >
      {children}
    </AccessControlContext.Provider>
  );
};
